import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axios, { CancelToken } from "axios";
import moment from "moment";
import useModal from "hooks/modals/useModal";

import { StatusCode } from "api/protocols";
import { GetBookingParticipant } from "api/rpc/2022-09/facilityAdmin/teesheet/booking";
import { GetReservationBookingAll } from "api/rpc/facilityAdmin/reservation/reservation";

import { dequeue, enqueue, showError, showSuccess } from "redux/actions/ui";

import { convertTime, valueToString } from "helpers/Helpers";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { useCustomerContext } from "../../context/CustomerContext";

import { Badge } from "components/badge/Badge";
import DataTable from "../../../../../../components/dataTable/DataTable";
import { Select } from "components/select/index";
import "./customerReservations.scss";
import { useHistory } from "react-router";
import "../../main/customer.scss";
import ReactDOM from "react-dom";
import { THIS_YEAR } from "components/form/calendarField/helpers";
import { NavigationDropdownNew } from "components/navigationDropdownNew/NavigationDropdownNew";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisVertical } from "@fortawesome/pro-regular-svg-icons";
import Portal from "elements/Portal";
import AuditTrailModal from "pages/secure/facility/teesheet/newTeeSheet/modals/AuditTrailModal";
// import { ITeeTimeTabNew } from "pages/secure/facility/teesheet/newTeeSheet/NewTeeSheet";
import { IBookingParticipant } from "redux/reducers/models/teetime";
import Popup from "components/popup/Popup";
import {
  PutBookingParticipantNoShow,
  PutBookingParticipant,
  GetBooking,
} from "api/rpc/2024-04/facilityAdmin/teesheet/booking";

import { PostLineItemsToOrder, PostOrder } from "api/rpc/2024-04/facilityAdmin/order/order";
import { PostTransaction, PutCapture } from "api/rpc/2024-04/facilityAdmin/order/transaction";
import Checkbox from "components/form/checkbox/Checkbox";
import { useStripe } from "@stripe/react-stripe-js";
import Sheet from "components/sheet/Sheet";
import Spin from "components/spin/spin";
import { LocaleCurrency } from "helpers/Locale";
import { IProduct } from "redux/reducers/models/product";
import Toggle from "components/form/toggle/Toggle";
import { INoShowChargeModal, INoShowModal } from "pages/secure/facility/teesheet/newTeeSheet/NewTeeSheetTabs";
import { GetProduct } from "api/rpc/2024-04/facilityAdmin/product/product";
import useCancelToken from "hooks/useCancelToken/useCancelToken";
import { GetCustomerPaymentMethod } from "api/rpc/2024-04/facilityAdmin/customer/customer";

const TABLE_LIMIT = 25;

export default function CustomerReservations() {
  const { state, updateState } = useCustomerContext();
  const history = useHistory();
  const facilityStore = useAppSelector(store => store.facilityStore);
  const stripe = useStripe();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [selectedYear, setSelectedYear] = useState<string>(null);
  const { Option } = Select;
  const { cancelToken, cancelRequest } = useCancelToken();
  const [offsetState, setOffsetState] = useState<number>(0);

  const [auditTrailState, updateAuditTrailState] = useState({
    open: false,
    teetime: null,
    bookingId: null,
    type: null,
  });

  const {
    state: noShowChargeModal,
    updateModal: updateNoShowChargeModal,
    closeModal: closeNoShowChargeModal,
  } = useModal<INoShowChargeModal>({
    clearOnClose: true,
    booking: null,
    selectedProduct: null,
    noShowProducts: null,
    paymentMethod: null,
    confirmationPopup: false,
    sendNoShowChargeEmail: false,
  });

  const {
    state: noShowModal,
    updateModal: updateNoShowModal,
    closeModal: closeNoShowModal,
  } = useModal<INoShowModal>({
    clearOnClose: true,
    type: "charge",
    bookingParticipantIds: [],
    slotGroup: null,
    teeTimeIndex: null,
    chargeNoShow: false,
    bookingId: null,
  });

  const {
    state: noCreditCardPopup,
    updateModal: updateNoCreditCardPopup,
    closeModal: closeNoCreditCardPopup,
  } = useModal({
    clearOnClose: true,
  });

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadReservationBookings(state.customer.id, source.token);
    return () => {
      source.cancel("Customer Reservations unmounted.");
    };
  }, [state.customer.id]);

  // Table OFFSET for booking history
  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadBooking(state.customer.id, offsetState, source.token);
    return () => {
      source.cancel("Customer Reservations unmounted.");
    };
  }, [state.customer.id, offsetState, selectedYear]);

  async function loadBooking(customerId: number, offset: number, token?: CancelToken) {
    if (state.bookings !== null) {
      updateState({ bookings: null });
    }
    const bookingRes = await GetBookingParticipant(
      {
        customer_id: customerId,
        extended_booking: true,
        offset,
        year: selectedYear,
      },
      token ? false : true,
      token,
    );

    if (token && token.reason) {
      return;
    }
    if (bookingRes.status !== StatusCode.OK) {
      dispatch(showError(String(t("Error obtaining customer bookings"))));
      updateState({ bookings: [] });
      return;
    }

    updateState({ bookings: bookingRes.data });
  }

  async function loadReservationBookings(customerId: number, token?: CancelToken) {
    if (state.reservationBookings !== null) {
      updateState({ reservationBookings: null });
    }
    const bookingRes = await GetReservationBookingAll({ customer_id: customerId }, token ? false : true, token);
    if (token && token.reason) {
      return;
    }
    if (bookingRes.status !== StatusCode.OK) {
      dispatch(showError(String(t("Error obtaining customer reservation bookings: "))));
      updateState({ reservationBookings: [] });
      return;
    }

    updateState({ reservationBookings: bookingRes.data });
  }

  function handleMoveToBooking(teetime_id: number) {
    history.push(`/admin/teesheet/details/${teetime_id}`);
  }

  function handleSelectedYear(year: string) {
    ReactDOM.unstable_batchedUpdates(() => {
      setOffsetState(0);
      setSelectedYear(year);
    });
  }

  function renderCustomerStatsGroup(screenSize: "desktop" | "mobile") {
    if (state.bookingCounts.length <= 0) {
      return null;
    }
    return (
      <div className={"booking-stats-general"}>
        <div className="customer-reservation_overview_container">
          <div className={`customer-stats-group-${screenSize}`}>
            <div className="customer-reservation_stats_container">
              <span className="customer-stat">
                <p className="stat-title">Bookings</p>
                <p className="stat-number">
                  {state.bookingCounts?.find((count: any) => count.year === selectedYear)?.count}
                </p>
              </span>
              <span className="customer-stat">
                <p className="stat-title">No Shows</p>
                <p className="stat-number stat-number_warning">
                  {state.bookingCounts?.find((count: any) => count.year === selectedYear)?.no_shows}
                </p>
              </span>
              <span className="customer-stat">
                <p className="stat-title">Weather</p>
                <p className="stat-number stat-number_warning">
                  {state.bookingCounts?.find((count: any) => count.year === selectedYear)?.weather_no_shows}
                </p>
              </span>
            </div>
            {/* 
            <span className="customer-stat">
            <p className="stat-number">{state.bookingCount.no_show_weather}</p>
            <p className="stat-title">Rain Checks</p>
          </span>
            {screenSize === "desktop" && <span className="customer-stats-group-desktop-empty-space" />} */}
          </div>

          <div>
            <Select
              defaultValue={THIS_YEAR.toString()}
              onChange={(count: string) => handleSelectedYear(count)}
              //disabled={state.accountStatements === null || state.accountTransactions === null}
            >
              {state.bookingCounts?.map(count => {
                return (
                  <Option key={count.year} value={count.year} name={count.year}>
                    {count.year}
                  </Option>
                );
              })}
            </Select>
          </div>
        </div>
      </div>
    );
  }

  function handleOpenAuditTrail(bookingParticipant: IBookingParticipant) {
    updateAuditTrailState(prevState => ({
      ...prevState,
      bookingId: bookingParticipant?.booking?.id,
      teetime: bookingParticipant?.tee_time,
      open: true,
      type: "booking",
    }));
  }

  async function handleNoShow(chargeNoShow: boolean) {
    dispatch(enqueue());

    const putBookingParticipantRes = await PutBookingParticipantNoShow(
      {
        id: noShowModal?.bookingParticipantIds[0],
        no_show_type: noShowModal?.type,
        variant_id: noShowChargeModal?.selectedProduct?.id,
        sendEmail: noShowChargeModal?.sendNoShowChargeEmail,
      },
      false,
    );

    dispatch(dequeue());

    if (putBookingParticipantRes.status !== StatusCode.OK) {
      dispatch(
        showError(
          typeof putBookingParticipantRes?.data === "string"
            ? putBookingParticipantRes?.data
            : t("Error marking participant as ") + valueToString(noShowModal.type),
        ),
      );
      return;
    }

    dispatch(showSuccess(t("Successfully marked participant as {{type}}", {type: valueToString(noShowModal.type)})));

     updateNoShowModal({
      isOpen: false,
      type: "charge",
      bookingParticipantIds: [],
      bookingId: null,
      chargeNoShow: false,
    });

    //Charge booking participants
    if (chargeNoShow) {
      await handleNoShowCharge();
    }

    // Manually update the participants' no show values
    void loadBooking(state.customer.id, offsetState);
  }

  async function handleNoShowCharge(): Promise<void> {
    const manualCardPaymentOption = facilityStore?.paymentOptions?.find(
      paymentOption => paymentOption.payment_method === "manual_card",
    );

    if (
      !noShowChargeModal?.paymentMethod ||
      !noShowChargeModal?.selectedProduct ||
      !state?.customer ||
      !manualCardPaymentOption
    ) {
      console.error(t("No show selection error"));
      return null;
    }

    dispatch(enqueue());
    //Create order
    const postOrderRes = await PostOrder({ customer_id: noShowChargeModal?.booking?.customer_id }, false);
    if (postOrderRes?.status !== StatusCode.OK) {
      dispatch(showError(String(t("Error creating order"))));
      dispatch(dequeue());
      return null;
    }

    //Create line item order
    const postLineItemOrderRes = await PostLineItemsToOrder(
      {
        order_id: postOrderRes.data.id,
        variant_id: noShowChargeModal?.selectedProduct?.id,
        quantity: 1,
      },
      false,
    );

    if (postLineItemOrderRes?.status !== StatusCode.OK) {
      dispatch(showError(String(t("Error adding line items to order"))));
      dispatch(dequeue());
      return null;
    }

    //Create transaction
    const postTransactionRes = await PostTransaction(
      {
        kind: "sale",
        amount: postLineItemOrderRes.data?.total_price,
        order_id: postOrderRes.data?.id,
        payment_option_id: manualCardPaymentOption?.id,
      },
      false,
    );

    if (postTransactionRes?.status !== StatusCode.OK) {
      dispatch(showError(String(t("Error creating transaction"))));
      dispatch(dequeue());
      return null;
    }

    if (
      postTransactionRes.data.client_secret &&
      noShowChargeModal?.paymentMethod?.stripe_payment_method_id &&
      postTransactionRes.data.id &&
      postOrderRes.data.id
    ) {
      //Make the card payment
      const participantRes = await confirmCardPaymentNoShow(
        postTransactionRes.data.client_secret,
        String(noShowChargeModal?.paymentMethod?.stripe_payment_method_id),
        postTransactionRes.data.id,
        Number(postOrderRes.data.id),
      );
      if (participantRes) {
        dispatch(dequeue());
        return null;
      }
    } else {
      dispatch(showError(String(t("Error creating card payment"))));
      dispatch(dequeue());
      return null;
    }

    dispatch(dequeue());
    return null;
  }

  // Confirm Stripe payment intent
  async function confirmCardPaymentNoShow(
    clientSecret: string,
    paymentMethodID: string,
    trans_id: number,
    orderId: number,
  ) {
    if (!stripe) {
      console.error("Stripe hook error");
      return null;
    }
    const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
      payment_method: paymentMethodID,
    });

    if (stripeError) {
      dispatch(showError(stripeError.message));
      return null;
    }

    //Capture the payment
    const captureRes = await PutCapture({ payment_intent_id: paymentIntent.id, transaction_id: trans_id }, false);

    if (captureRes.status !== StatusCode.OK) {
      dispatch(showError(String(t("Error capturing payment"))));
      return null;
    }

    //Update no show paid value and add order to the participants
    const participantRes = await PutBookingParticipant(
      { id: state.customer.id, no_show_paid: true, order_id: orderId },
      false,
    );

    if (participantRes.status !== StatusCode.OK) {
      dispatch(showError(String(t("Error updating a participant's no show status"))));
    }

    dispatch(showSuccess(String(t("Successfully charged participants as no show"))));
    void closeNoShowChargeModal();
    void closeNoShowModal();
    return participantRes;
  }

  async function getCustomerPaymentMethod(paymentMethodId: number, customerId: number) {
    const paymentMethodRes = await GetCustomerPaymentMethod({ id: paymentMethodId, customer_id: customerId }, false);

    if (paymentMethodRes?.status !== StatusCode.OK) {
      void closeNoShowChargeModal();
      dispatch(showError(String(t("Error getting payment method on the booking"))));
      return;
    }

    updateNoShowChargeModal({ paymentMethod: paymentMethodRes?.data[0] });
  }

  async function getNoShowBooking() {
    const noShowCancelToken = cancelToken();
    //Show loader
    updateNoShowChargeModal({ isOpen: true, booking: undefined });
    if (!noShowModal?.bookingId) {
      dispatch(showError(String(t("Booking not found"))));
      void closeNoShowChargeModal();
      return;
    }

    const bookingRes = await GetBooking({ id: noShowModal?.bookingId }, false, noShowCancelToken);
    if (bookingRes?.status !== StatusCode.OK) {
      void closeNoShowChargeModal();
      if (noShowCancelToken?.reason) {
        return;
      }

      dispatch(showError(String(t("Error getting booking"))));
      return;
    }
    if (bookingRes?.data?.length > 0) {
      const booking = bookingRes?.data[0];
      if (!booking?.customer_payment_method_id) {
        void closeNoShowChargeModal();
        updateNoCreditCardPopup({ isOpen: true });
        return;
      }
      const noShowProducts = await GetProduct({ type: "No Show Green Fee", extended: true }, false, noShowCancelToken);
      if (noShowProducts?.status !== StatusCode.OK) {
        void closeNoShowChargeModal();
        if (noShowCancelToken?.reason) {
          return;
        }
        dispatch(showError(String(t("Error getting No Show Products"))));
        return;
      }
      //Get the customer payment method information
      void getCustomerPaymentMethod(Number(booking?.customer_payment_method_id), Number(booking?.customer_id));
      updateNoShowChargeModal({ isOpen: true, booking: booking, noShowProducts: noShowProducts?.data });
    } else {
      dispatch(showError(String(t("Booking not found"))));
      void closeNoShowChargeModal();
      return;
    }
  }

  return (
    <>
      <div>
        <div>{renderCustomerStatsGroup("desktop")}</div>
      </div>

      <h1 className="customer-reservation-header">
        {t("Tee Time Booking History")}
      </h1>
      <DataTable
        columns={[
          { label: t("Confirmation No.") },
          { label: t("Tee Time") },
          { label: t("Booked On") },
          { label: t("Course") },
          { label: t("Golfers") },
          { label: t("Status") },
          { label: "" },
        ]}
        loading={state.bookings === null}
        footer={{
          tableLimit: TABLE_LIMIT,
          tableOffset: offsetState,
          handleTableOffset: direction =>
            setOffsetState(direction === "prev" ? offsetState - TABLE_LIMIT : offsetState + TABLE_LIMIT),
          disableNextOffset: !state.bookings || !(state.bookings?.length === TABLE_LIMIT),
        }}
      >
        {state.bookings
          ?.filter(bookingParticipant => !!bookingParticipant.turn === false)
          ?.map(bookingParticipant => {
            return (
              <tr key={bookingParticipant.id}>
                <td
                  onClick={
                    facilityStore.facility?.id === bookingParticipant.tee_time?.course_id
                      ? () => handleMoveToBooking(bookingParticipant.tee_time.id)
                      : null
                  }
                  className={
                    facilityStore.facility?.id === bookingParticipant.tee_time?.course_id
                      ? "customer-reservation_confirmation_number"
                      : ""
                  }
                >
                  {bookingParticipant.booking.confirmation_number}
                </td>

                {bookingParticipant.tee_time ? (
                  <td>
                    <span>{moment(bookingParticipant.tee_time.date).format("MMMM DD YYYY")}</span>
                    <span>
                      {" "}
                      {t("at")}{" "}
                      {convertTime(bookingParticipant.tee_time.start_time)}
                    </span>
                  </td>
                ) : (
                  <td>{t("N/A")}</td>
                )}

                {bookingParticipant.created_at ? (
                  <td>
                    <span>{moment(bookingParticipant.created_at).format("MMMM DD YYYY, h:mm A")}</span>
                  </td>
                ) : (
                  <td>{t("N/A")}</td>
                )}

                {bookingParticipant.tee_time ? (
                  <td>{bookingParticipant.tee_time.course.long_name}</td>
                ) : (
                  <td>{t("N/A")}</td>
                )}

                <td>{bookingParticipant.booking.quantity}</td>
                <td>


                  {generateStatusBadge(
                    bookingParticipant.status === "complete"
                      ? bookingParticipant.check_in_status
                      : bookingParticipant.status,
                      bookingParticipant.no_show_type,
                  )}
                </td>

                <td>
                  <NavigationDropdownNew
                    showPlainTextLabel
                    rightAlign
                    label={<FontAwesomeIcon icon={faEllipsisVertical} />}
                    sections={[
                      [
                        {
                          icon: "calendar-check",
                          label: t("View Details"),
                          onClick:
                            facilityStore.facility?.id === bookingParticipant.tee_time?.course_id
                              ? () => handleMoveToBooking(bookingParticipant.tee_time.id)
                              : null,
                        },
                        {
                          label: t("Change no show status"),
                          icon: "arrows-rotate",
                          onClick: () =>
                            updateNoShowModal({
                              isOpen: true,
                              bookingParticipantIds: [bookingParticipant?.id],
                              bookingId: bookingParticipant?.booking?.id,
                            }),
                        },
                        {
                          label: t("Audit Trail"),
                          icon: "route",
                          onClick: () => handleOpenAuditTrail(bookingParticipant),
                        },
                      ],
                    ]}
                  />
                </td>
              </tr>
            );
          })}
      </DataTable>

      <h1 className="customer-reservation-header">
        {t("Reservation History")}
      </h1>

      <DataTable
        columns={[
          { label: t("Confirmation No.") },
          { label: t("Date") },
          { label: t("Start Time") },
          { label: t("Players") },
        ]}
        loading={state.reservationBookings === null}
      >
        {state.reservationBookings?.map(reservation => {
          return (
            <tr key={reservation.id}>
              <td>{reservation.confirmation_number}</td>
              <td>{moment(reservation.date).format("LL")}</td>
              <td>{moment(reservation.start_time, "hh:mm:ss").format("h:mm A")}</td>
              <td>{reservation.quantity}</td>
            </tr>
          );
        })}
      </DataTable>

      {/* AUDIT TRAIL MODAL */}
      <Portal isMounted={auditTrailState.open}>
        <AuditTrailModal
          open={auditTrailState.open}
          teetime={auditTrailState.teetime}
          bookingId={auditTrailState.bookingId}
          onCancel={() =>
            updateAuditTrailState({
              open: false,
              teetime: null,
              type: null,
              bookingId: null,
            })
          }
          type={auditTrailState.type}
        />
      </Portal>

      {/* NO SHOW MODAL */}
      <Portal isMounted={noShowModal.isOpen}>
        <Popup
          open={noShowModal.isOpen}
          type="info"
          title={t("Mark As No Show")}
          description={t("Select the type of no show you would like to apply to this customer.")}
          onOk={() => (noShowModal?.chargeNoShow ? getNoShowBooking() : handleNoShow(false))}
          okText={noShowModal.chargeNoShow ? t("Continue") : t("Confirm")}
          okDisabled={!noShowModal.type}
          cancelText={t("Cancel")}
          onCancel={() =>
            updateNoShowModal({
              isOpen: false,
              type: "charge",
              bookingParticipantIds: [],
              bookingId: null,
              teeTimeIndex: null,
            })
          }
        >
          <br />
          <Select defaultValue={noShowModal.type} onChange={(value: string) => updateNoShowModal({ type: value })}>
            <Select.Option value={"charge"} name={"Charge"}>
              <span>{t("Charge")}</span>
            </Select.Option>
            <Select.Option value={"defer"} name={"Defer Charge"}>
              <span>{t("Defer Charge")}</span>
            </Select.Option>
            <Select.Option value={"weather"} name={"Weather"}>
              <span>{t("Weather")}</span>
            </Select.Option>
          </Select>
          <br />

          <div className="flex justify-right">
            <Checkbox
              size="small"
              value={noShowModal?.chargeNoShow}
              checked={noShowModal?.chargeNoShow}
              onChange={e => updateNoShowModal({ chargeNoShow: e.target.checked })}
              label={t("Charge Customers")}
            />
          </div>
        </Popup>
      </Portal>

      {/* NO SHOW MODAL */}
      {/* <Portal isMounted={noShowState.open}>
        <Popup
          open={noShowState.open}
          type="info"
          title={t("Mark As No Show")}
          description={t("Select the type of no show you would like to apply to this customer.")}
          // onOk={handleNoShow}

           onOk={() => noShowState?.chargeNoShow ? getNoShowBooking() : handleNoShow}
           okText={
            noShowState?.chargeNoShow
              ? t("Continue")
              : t("Confirm")
          }
          okDisabled={!noShowState.type}
          cancelText={t("Cancel")}
          onCancel={() => changeNoShowState(prevState => ({ ...prevState, open: false }))}
        >
          <br />
          <Select
            defaultValue={noShowState.type}
            onChange={(value: string) => changeNoShowState(prevState => ({ ...prevState, type: value }))}
          >
            <Select.Option value={"charge"} name={"Charge"}>
              <span>{t("Charge")}</span>
            </Select.Option>
            <Select.Option value={"defer"} name={"Defer Charge"}>
              <span>{t("Defer Charge")}</span>
            </Select.Option>
            <Select.Option value={"weather"} name={"Weather"}>
              <span>{t("Weather")}</span>
            </Select.Option>
          </Select>
          <br />
          <div className="flex justify-right">
            <Checkbox
              size="small"
              value={noShowState?.chargeNoShow}
              checked={noShowState?.chargeNoShow}
              onChange={e => changeNoShowState(prev => ({ ...prev, chargeNoShow: e.target.checked }))}
              label={t("Charge Customers")}
            />
          </div>
        </Popup>
      </Portal> */}

      {/* CHARGE NO SHOW MODAL */}
      <Portal isMounted={noShowChargeModal?.isOpen}>
        <Sheet
          open={noShowChargeModal?.isOpen}
          size="medium"
          closable
          title={t("Charge No Show")}
          onCancel={() => {
            closeNoShowChargeModal();
            cancelRequest();
          }}
          onOk={() => updateNoShowChargeModal({ confirmationPopup: true })}
          cancelText={t("Cancel")}
          okText={t("Charge")}
          stacked
          okDisabled={!noShowChargeModal?.paymentMethod || !noShowChargeModal?.selectedProduct}
          overflow
        >
          {noShowChargeModal?.booking === undefined ? (
            <div style={{ display: "flex", margin: "auto", height: "100%" }}>
              <div className="text-center m-auto">
                <div style={{ height: "32px" }}>
                  <Spin />
                </div>
                <div>Loading...</div>
              </div>
            </div>
          ) : (
            <div className="h-full flex flex-col">
              <Select
                label={t("secure.facility.tee_sheet.tee_sheet_booking_details.060")}
                onChange={(productId, product) => updateNoShowChargeModal({ selectedProduct: product })}
                defaultValue={noShowChargeModal.selectedProduct?.id}
              >
                {noShowChargeModal?.noShowProducts?.length >= 1 &&
                  noShowChargeModal?.noShowProducts?.map((item: IProduct) => {
                    const productTitle = item?.title;
                    const preferredTitle = item?.preferred_title;
                    return (
                      item.variants &&
                      item.variants
                        .filter(variantFiltered => variantFiltered.price > 0)
                        .map((variant, i) => {
                          return (
                            <Select.Option key={i} value={variant.id} name={variant.title} extraValues={variant}>
                              <div>
                                <span>{preferredTitle ? preferredTitle : productTitle} </span>
                                {productTitle !== variant.title && (
                                  <span className="text-subdued text-sm">{variant.title}</span>
                                )}
                                <span>
                                  {" "}
                                  - <LocaleCurrency currency="cad" amount={variant.price} />
                                </span>
                              </div>
                            </Select.Option>
                          );
                        })
                    );
                  })}
              </Select>

              <div className="mt-auto text-lg">
                <ul>
                  <li className="flex justify-end">
                    <span className="mr-4 text-medium">{noShowChargeModal?.selectedProduct?.title}</span>
                    <LocaleCurrency currency="cad" amount={noShowChargeModal?.selectedProduct?.price ?? 0} />
                  </li>
                  <li className="flex justify-end">
                    <span className="mr-4 text-medium">
                      {t("secure.facility.tee_sheet.tee_sheet_booking_details.063")}
                    </span>
                    <span>{1}</span>
                  </li>
                  <li className="flex justify-end text-bold">
                    <span className="mr-4">{t("secure.facility.tee_sheet.tee_sheet_booking_details.064")}</span>
                    <LocaleCurrency
                      currency="cad"
                      amount={noShowChargeModal?.selectedProduct ? noShowChargeModal?.selectedProduct?.price : 0}
                    />
                  </li>
                </ul>
                <div>
                  <Toggle
                    label={t("Send No Show Charge Email")}
                    id="sendNoShowChargeEmail"
                    checked={noShowChargeModal?.sendNoShowChargeEmail}
                    onChange={e => updateNoShowChargeModal({ sendNoShowChargeEmail: e.target.checked })}
                  />
                </div>
              </div>
            </div>
          )}
        </Sheet>
      </Portal>

      {/* NO SHOW CHARGE CONFIRMATION */}
      <Portal isMounted={noShowChargeModal?.confirmationPopup}>
        <Popup
          onCancel={() => updateNoShowChargeModal({ confirmationPopup: false })}
          onOk={() => handleNoShow(true)}
          okText={t("Charge")}
          open={noShowChargeModal?.confirmationPopup}
          type="info"
          title={t("Confirm No Show Charge")}
          stacked
          description={`${t("Are you sure you want to apply a no show charge to the payment method ending in")} ${noShowChargeModal?.paymentMethod?.last4} ${t(
            "attached to this booking?",
          )}`}
        />
      </Portal>

      <Portal isMounted={noCreditCardPopup.isOpen}>
        <Popup
          open={noCreditCardPopup.isOpen}
          type="info"
          title={t("No Payment Method on Booking")}
          description={t("There is no payment method saved on this booking")}
          onOk={closeNoCreditCardPopup}
          okText={t("Close")}
          stacked
        />
      </Portal>
    </>
  );
}

function generateStatusBadge(status: string, no_show_status?: string) {

  console.log(status, no_show_status);

  switch (status) {
    case "checked":
      return <Badge type="success">{valueToString(status, "capitalize")}</Badge>;
    case "unchecked":
      return;
    case "cancelled":
      return <Badge type="error">{valueToString(status, "capitalize")}</Badge>;
    case "no_show":

      switch (no_show_status) {
        case "defer":
          return <Badge type="error">{valueToString(no_show_status, "capitalize")}</Badge>;
        case "charge":
          return <Badge type="error">{valueToString(no_show_status, "capitalize")}</Badge>;
        case "weather":
          return <Badge type="warning">{valueToString(no_show_status, "capitalize")}</Badge>;
      
        default:
      return <Badge type="error">{valueToString(status, "capitalize")}</Badge>;

      }


    default:
      return <Badge type="gray">{valueToString(status, "capitalize")}</Badge>;
  }
}
